import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import AssignmentControl from "../../components/Tickets/AssignmentControl";
import { useLayout } from "../../context/LayoutContext";
import {
  API_LIST_TICKETS,
  API_UPDATE_TICKET_PRIORITY,
  API_UPDATE_TICKET_STATUS,
} from "../../api/ticket.service";
import { assignSupportTicket } from "../../api/ticketAssignment.service";
import TicketChatPanel from "../../components/TicketChat/TicketChatPanel";
import {
  normalizeAssignedTo,
  TICKET_AGENT_OPTIONS,
} from "../../constants/ticketAgents";
import "./ticketDetail.css";

const statusOptions = ["Open", "In Progress", "Resolved", "Closed"];
const priorityOptions = ["Low", "Medium", "High", "Urgent"];

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return date.toLocaleString();
};

export const TicketDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toastMessage } = useLayout();
  const currentUser = useSelector((state) => state.authenticate.user);
  const [ticket, setTicket] = useState(location.state?.ticket || null);
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    status: location.state?.ticket?.status || "",
    priority: location.state?.ticket?.priority || "",
  });
  const [assignedDraft, setAssignedDraft] = useState(
    normalizeAssignedTo(location.state?.ticket?.assignedTo)
  );
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [dirty, setDirty] = useState(false);
  const token = localStorage.getItem("token");

  const fetchById = async () => {
    const companyId = localStorage.getItem("ticketsCompanyId");
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await API_LIST_TICKETS({
        companyId: Number(companyId),
        q: String(id),
        page: 0,
        limit: 1,
      });
      const row = res?.data?.rows?.[0] || null;
      setTicket(row);
      if (row) {
        setFormState({
          status: row.status || "",
          priority: row.priority || "",
        });
        setAssignedDraft(normalizeAssignedTo(row.assignedTo));
      }
      // Silent failure per request: do not show error message.
    } catch (err) {
      // Silent failure per request: do not show error message.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ticket) {
      fetchById();
    }
  }, []);

  const handleFieldChange = (field, value) => {
    setFormState((prev) => {
      const next = { ...prev, [field]: value };
      setDirty(
          next.status !== (ticket?.status || "") ||
          next.priority !== (ticket?.priority || "")
      );
      return next;
    });
  };

  const handleSave = async () => {
    if (!ticket || !dirty) return;
    setLoading(true);
    try {
      if (formState.status !== ticket.status) {
        await API_UPDATE_TICKET_STATUS(ticket.id, formState.status);
      }
      if (formState.priority !== ticket.priority) {
        await API_UPDATE_TICKET_PRIORITY(ticket.id, formState.priority);
      }
      setTicket((prev) => ({
        ...prev,
        status: formState.status,
        priority: formState.priority,
      }));
      setDirty(false);
      // Silent success per request: do not show message.
    } catch (err) {
      // Silent failure per request: do not show error message.
    } finally {
      setLoading(false);
    }
  };

  const applyAssignedUpdate = (responseData, fallbackAssignedTo) => {
    const updatedTicket = responseData?.data || null;
    const nextAssignedTo =
      normalizeAssignedTo(updatedTicket?.assignedTo) ||
      normalizeAssignedTo(fallbackAssignedTo);

    setTicket((prev) =>
      prev
        ? {
            ...prev,
            ...(updatedTicket || {}),
            assignedTo: nextAssignedTo,
          }
        : prev
    );
    setAssignedDraft(nextAssignedTo);
    setAssignError("");
  };

  const handleAssign = async () => {
    if (!ticket || assignLoading) return;
    try {
      setAssignLoading(true);
      const response = await assignSupportTicket(ticket.id, assignedDraft);
      applyAssignedUpdate(response, assignedDraft);
      toastMessage({ severity: "success", detail: "Ticket assigned successfully" });
    } catch (error) {
      const message = error?.message || "Unable to assign ticket";
      setAssignError(message);
      toastMessage({ severity: "error", detail: message });
    } finally {
      setAssignLoading(false);
    }
  };

  const handleUnassign = async () => {
    if (!ticket || assignLoading) return;
    try {
      setAssignLoading(true);
      const response = await assignSupportTicket(ticket.id, null);
      applyAssignedUpdate(response, "");
      toastMessage({ severity: "success", detail: "Ticket unassigned successfully" });
    } catch (error) {
      const message = error?.message || "Unable to unassign ticket";
      setAssignError(message);
      toastMessage({ severity: "error", detail: message });
    } finally {
      setAssignLoading(false);
    }
  };

  const slaCountdown = useMemo(() => {
    if (!ticket?.slaDueAt) return { label: "No SLA", overdue: false };
    const due = new Date(ticket.slaDueAt).getTime();
    const now = Date.now();
    const diff = due - now;
    if (diff <= 0) return { label: "Overdue", overdue: true };
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { label: `${hours}h ${mins}m left`, overdue: false };
  }, [ticket?.slaDueAt]);

  const activityLog = useMemo(() => {
    if (!ticket) return [];
    return [
      {
        label: "Ticket Created",
        detail: `Created by user ${ticket.createdBy}`,
        time: formatDate(ticket.createdAt),
      },
      {
        label: "Status changed",
        detail: ticket.status ? `Status set to ${ticket.status}` : "Status set",
        time: formatDate(ticket.updatedAt),
      },
      {
        label: "Assigned",
        detail: ticket.assignedTo
          ? `Assigned to Agent ${ticket.assignedTo}`
          : "Unassigned",
        time: formatDate(ticket.updatedAt),
      },
    ];
  }, [ticket]);

  if (!ticket && !loading) {
    return (
      <div className="ticket-detail-page">
        <div className="ticket-empty-card">
          <p>Open from list to see full details.</p>
          <button className="primary-action" onClick={() => navigate("/tickets")}>
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-detail-page">
      {loading && (
        <div className="loading-container">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        </div>
      )}
      {ticket && (
        <>
          <div className="ticket-detail-header-card">
            <div>
              <h1 className="ticket-title">
                {ticket.subject || "Ticket Detail"}
              </h1>
              <div className="ticket-subtitle">
                <span>SKBT-{String(ticket.id).padStart(3, "0")}</span>
                <span className="dot-sep">•</span>
                <span>
                  {ticket.companyId ? `Company #${ticket.companyId}` : "Company"}
                </span>
              </div>
            </div>
            <div className="ticket-header-badges">
              <span className={`badge badge-${(ticket.status || "open").toLowerCase().replace(" ", "-")}`}>
                {ticket.status || "Status"}
              </span>
              <span className={`badge badge-priority-${(ticket.priority || "low").toLowerCase()}`}>
                {ticket.priority || "Priority"}
              </span>
              <span className={`badge ${slaCountdown.overdue ? "badge-danger" : "badge-neutral"}`}>
                SLA: {slaCountdown.label}
              </span>
            </div>
          </div>

          <div className="ticket-detail-layout">
            <div className="ticket-detail-main">
              <div className="ticket-info-row">
                <div className="ticket-card">
                  <div className="ticket-card-header">
                    <h3>Customer Information</h3>
                    <span>User ID: {ticket.createdBy || "-"}</span>
                  </div>
                  <div className="ticket-info-grid">
                    <div>
                      <label>Name</label>
                      <p>{ticket.name || "-"}</p>
                    </div>
                    <div>
                      <label>Email</label>
                      <p>{ticket.email || "-"}</p>
                    </div>
                    <div>
                      <label>Company</label>
                      <p>
                        {ticket.companyId ? `Company #${ticket.companyId}` : "-"}
                      </p>
                    </div>
                    <div>
                      <label>Ticket ID</label>
                      <p>SKBT-{String(ticket.id).padStart(3, "0")}</p>
                    </div>
                  </div>
                </div>
                <div className="ticket-card">
                  <div className="ticket-card-header">
                    <h3>Description</h3>
                  </div>
                  <div className="ticket-description-box">
                    {ticket.description || "No description provided."}
                  </div>
                </div>
              </div>

         

              <div className="ticket-card">
                <div className="ticket-card-header">
                  <h3>Attachments</h3>
                </div>
                <div className="ticket-attachments-list">
                  {ticket.attachmentUrls?.length ? (
                    ticket.attachmentUrls.map((url, index) => (
                      <div key={`${url}-${index}`} className="attachment-row">
                        <div>
                          <strong>Attachment {index + 1}</strong>
                        </div>
                        <a href={url} target="_blank" rel="noreferrer">
                          View
                        </a>
                      </div>
                    ))
                  ) : (
                    <p>No attachments</p>
                  )}
                </div>
              </div>

              <TicketChatPanel
                ticketId={ticket.id || id}
                companyId={ticket.companyId}
                token={token}
                currentUser={currentUser}
              />

              <div className="ticket-card">
                <div className="ticket-card-header">
                  <h3>Timeline</h3>
                </div>
                <div className="ticket-timeline-list">
                  {activityLog.map((event, index) => (
                    <div className="timeline-item" key={`${event.label}-${index}`}>
                      <span className="timeline-dot" />
                      <div>
                        <strong>{event.label}</strong>
                        <p>{event.detail}</p>
                        <span>{event.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <aside className="ticket-detail-aside">
              <div className="ticket-card">
                <div className="ticket-card-header">
                  <h3>Ticket Actions</h3>
                </div>
                <div className="ticket-form">
                  <label>Status</label>
                  <select
                    value={formState.status}
                    onChange={(e) => handleFieldChange("status", e.target.value)}
                  >
                    <option value="">Select status</option>
                    {statusOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <label>Priority</label>
                  <select
                    value={formState.priority}
                    onChange={(e) => handleFieldChange("priority", e.target.value)}
                  >
                    <option value="">Select priority</option>
                    {priorityOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <button
                    className="primary-action"
                    onClick={handleSave}
                    disabled={!dirty || loading}
                  >
                    Save Changes
                  </button>
                </div>
              </div>

              <div className="ticket-card">
                <div className="ticket-card-header">
                  <h3>Assignment</h3>
                </div>
                <AssignmentControl
                  value={assignedDraft}
                  agentOptions={TICKET_AGENT_OPTIONS}
                  loading={assignLoading}
                  error={assignError}
                  onChange={(value) => {
                    setAssignError("");
                    setAssignedDraft(normalizeAssignedTo(value));
                  }}
                  onAssign={handleAssign}
                  onUnassign={handleUnassign}
                />
              </div>

              <div className="ticket-card">
                <div className="ticket-card-header">
                  <h3>SLA & Timing</h3>
                </div>
                <div className="sla-list">
                  <div>
                    <span>Created</span>
                    <strong>{formatDate(ticket.createdAt)}</strong>
                  </div>
                  <div>
                    <span>First Response</span>
                    <strong>{formatDate(ticket.firstResponseAt)}</strong>
                  </div>
                  <div>
                    <span>SLA Due</span>
                    <strong className={slaCountdown.overdue ? "danger" : ""}>
                      {formatDate(ticket.slaDueAt)}
                    </strong>
                  </div>
                </div>
                {slaCountdown.overdue && (
                  <div className="sla-warning">
                    SLA breached. Immediate action required.
                  </div>
                )}
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
};
